import { zodResolver } from '@hookform/resolvers/zod';
import { pick } from 'lodash-es';
import { FieldValues, UseFormProps } from 'react-hook-form';
import {
  brandsFormSchema,
  configurationFormSchema,
  contactFormSchema,
  customerSettingTemplateFormSchema,
  deliveryAndBillingFormSchema,
  entityCriteriaFormSchema,
  generalFormValidationSchema,
  internalStocksFormSchema,
  quoteBrandsFormSchema,
  quoteInformationFormSchema,
  redistributionBrandsFormSchema,
  suppliersFormSchema,
} from '~/connect/schemas';
import { CustomerUtils } from '~/connect/helpers';
import { ICustomerDetail } from '~/common/types';
import { CustomerFormStep, ICustomerForm } from '~/connect/types';

/**
 * No step edits these entity fields, but rawToForm spreads the whole entity into the defaults
 * and the submit merges every step's values, so they are sent back unchanged on update.
 */
type CustomerFormDefaultValues = ICustomerForm &
  Pick<ICustomerDetail, 'level' | 'isSupplier' | 'supplierTemplate'>;

type StepFormConfig = {
  schema: Parameters<typeof zodResolver>[0];
  /** Steps that validate while the user types, as opposed to on submit only */
  validateOnChange?: boolean;
  /**
   * Author's note : The Pick is really critical so we don't overwrite values between steps.
   * A step without keys starts empty, its values are filled by the step itself.
   */
  defaultValueKeys?: Array<keyof CustomerFormDefaultValues>;
};

export const CUSTOMER_STEP_FORM_CONFIG: Record<
  CustomerFormStep,
  StepFormConfig
> = {
  GENERAL_FORM: {
    schema: generalFormValidationSchema,
    validateOnChange: true,
    defaultValueKeys: [
      'customerCode',
      'name',
      'commercialName',
      'parent',
      'level',
      'isSupplier',
      'customerGroup',
      'logo',
      'customName',
      'redistributionMaxDelay',
      'locale',
      'legalForm',
      'rcsNumber',
      'vatNumber',
    ],
  },
  CRITERIA_FORM: {
    schema: entityCriteriaFormSchema,
    validateOnChange: true,
    defaultValueKeys: ['criteria'],
  },
  QUOTE_EXPRESS_FORM: {
    schema: quoteInformationFormSchema,
    defaultValueKeys: ['quoteInformation'],
  },
  CONTACT_FORM: {
    schema: contactFormSchema,
    validateOnChange: true,
    defaultValueKeys: ['contacts'],
  },
  DELIVERY_FORM: {
    schema: deliveryAndBillingFormSchema,
    validateOnChange: true,
    defaultValueKeys: ['deliveryInformation', 'billingInformation'],
  },
  CONFIGURATION_FORM: {
    schema: configurationFormSchema,
    validateOnChange: true,
    defaultValueKeys: ['configuration'],
  },
  SUPPLIERS_FORM: {
    schema: suppliersFormSchema,
    validateOnChange: true,
    defaultValueKeys: ['supplierConfigurations', 'supplierTemplate'],
  },
  BRANDS_FORM: { schema: brandsFormSchema },
  QUOTE_BRANDS_FORM: { schema: quoteBrandsFormSchema },
  REDISTRIBUTION_BRANDS_FORM: { schema: redistributionBrandsFormSchema },
  SETTING_TEMPLATE_FORM: { schema: customerSettingTemplateFormSchema },
  INTERNAL_STOCK_FORM: {
    schema: internalStocksFormSchema,
    defaultValueKeys: ['internalStocks'],
  },
};

export const buildStepFormOptions = <TValues extends FieldValues>(
  step: CustomerFormStep,
  defaultValues?: Partial<ICustomerForm>,
): UseFormProps<TValues> => {
  const config = CUSTOMER_STEP_FORM_CONFIG[step];

  return {
    ...(config.validateOnChange
      ? { mode: 'onChange', reValidateMode: 'onChange' }
      : {}),
    resolver: zodResolver(config.schema),
    defaultValues: config.defaultValueKeys
      ? pick(
          (defaultValues ??
            CustomerUtils.DEFAULT_VALUES) as Partial<CustomerFormDefaultValues>,
          config.defaultValueKeys,
        )
      : {},
  } as UseFormProps<TValues>;
};
